import * as React from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { getAppUpdateSettings, getUserTags, updateAppUpdateSettings } from '../Api';
import AppUpdateSettingsDto from '../models/dtos/AppUpdateSettingsDto';
import UserTagDefinitionDto from '../models/dtos/UserTagDefinitionDto';
import { BuildNumberAutocomplete, useAppVersionsForOsTypes } from '../utils/appVersionFields';

const ANDROID_OS = 'android';

export default function AppUpdateSettingsPage() {
    const [loading, setLoading] = React.useState(true);
    const [enabled, setEnabled] = React.useState(false);
    const [minBuildNumber, setMinBuildNumber] = React.useState<number | null>(null);
    const [playStoreUrl, setPlayStoreUrl] = React.useState('market://details?id=org.betup');
    const [forceUpdate, setForceUpdate] = React.useState(false);
    const [showBeforeLogin, setShowBeforeLogin] = React.useState(true);
    const [userTags, setUserTags] = React.useState<string[]>([]);
    const [userTagDefinitions, setUserTagDefinitions] = React.useState<UserTagDefinitionDto[]>([]);
    const [userTagsLoading, setUserTagsLoading] = React.useState(true);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [dialogTitle, setDialogTitle] = React.useState('');
    const [dialogMsg, setDialogMsg] = React.useState('');

    const { versions: registeredVersions, loading: versionsLoading } = useAppVersionsForOsTypes([ANDROID_OS]);

    const applyDto = (dto: AppUpdateSettingsDto | null) => {
        if (dto == null) return;
        setEnabled(dto.enabled ?? false);
        setMinBuildNumber(dto.minBuildNumber ?? null);
        setPlayStoreUrl(dto.playStoreUrl ?? 'market://details?id=org.betup');
        setForceUpdate(dto.forceUpdate ?? false);
        setShowBeforeLogin(dto.showBeforeLogin ?? true);
        setUserTags(dto.userTags ?? []);
    };

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setUserTagsLoading(true);
            const [dto, tags] = await Promise.all([
                getAppUpdateSettings(),
                getUserTags(),
            ]);
            if (!cancelled) {
                applyDto(dto);
                if (tags) {
                    setUserTagDefinitions(tags.slice().sort((a, b) => a.tagName.localeCompare(b.tagName)));
                }
                setUserTagsLoading(false);
                setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const findUserTagDefinition = React.useCallback(
        (tagName: string) =>
            userTagDefinitions.find(
                tag => tag.tagName.localeCompare(tagName, undefined, { sensitivity: 'accent' }) === 0,
            ),
        [userTagDefinitions],
    );

    const selectedUserTagOptions = React.useMemo(
        () =>
            userTags.map(tagName => findUserTagDefinition(tagName) ?? {
                id: tagName,
                tagName,
                requirements: [],
                approximateUsersCount: null,
                createdAt: '',
            }),
        [userTags, findUserTagDefinition],
    );

    const openDialog = (title: string, message: string) => {
        setDialogTitle(title);
        setDialogMsg(message);
        setDialogOpen(true);
    };

    const onSave = async () => {
        if (minBuildNumber == null || Number.isNaN(minBuildNumber) || minBuildNumber < 1) {
            openDialog('Error', 'Enter a valid minimum build number (users below this build will see the prompt).');
            return;
        }
        if (!playStoreUrl.trim()) {
            openDialog('Error', 'Play Store URL must not be empty.');
            return;
        }
        const payload: AppUpdateSettingsDto = {
            version: 1,
            enabled,
            minBuildNumber,
            playStoreUrl: playStoreUrl.trim(),
            forceUpdate,
            showBeforeLogin,
            userTags: userTags.map(tag => tag.trim().toLowerCase()).filter(Boolean),
        };
        const ok = await updateAppUpdateSettings(payload);
        if (!ok) {
            openDialog('Error', 'Failed to save. Check server logs and API configuration.');
            return;
        }
        const dto = await getAppUpdateSettings();
        applyDto(dto);
        openDialog('Saved', 'App update settings were saved. Android clients pick them up on the next app open.');
    };

    return (
        <Stack gap={3} sx={{ mx: 5, my: 5 }}>
            <Typography variant="h4">App update prompt (Android)</Typography>
            <Typography variant="body2" color="text.secondary">
                Control when outdated Android builds see an in-app update dialog. Stored in qbet_app_settings on the main API.
            </Typography>
            <Alert severity="info" sx={{ maxWidth: 'md' }}>
                <Typography variant="body2" component="div">
                    Users with <strong>build number &lt; minimum build</strong> are prompted. Use <strong>before login</strong> on splash
                    (user tags are ignored there) or <strong>after login</strong> in the main app (user tags apply).
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                    <strong>Force update</strong> blocks entering the app until the user opens the Play Store or exits.
                    <strong> Suggest update</strong> lets the user dismiss and continue.
                </Typography>
            </Alert>
            {loading ? (
                <Box><CircularProgress /></Box>
            ) : (
                <Grid container spacing={2} maxWidth="md">
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={<Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} />}
                            label="Enable app update prompt"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <BuildNumberAutocomplete
                            label="Minimum build number (prompt below this)"
                            value={minBuildNumber}
                            onChange={setMinBuildNumber}
                            versions={registeredVersions}
                            loading={versionsLoading}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Play Store URL"
                            fullWidth
                            value={playStoreUrl}
                            onChange={e => setPlayStoreUrl(e.target.value)}
                            helperText="market://details?id=org.betup or https://play.google.com/store/apps/details?id=org.betup"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={<Switch checked={forceUpdate} onChange={e => setForceUpdate(e.target.checked)} />}
                            label="Force update (block app until update)"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showBeforeLogin}
                                    onChange={e => setShowBeforeLogin(e.target.checked)}
                                />
                            }
                            label="Show before login (splash)"
                        />
                    </Grid>
                    {!showBeforeLogin && (
                        <Grid item xs={12}>
                            <Autocomplete
                                multiple
                                options={userTagDefinitions}
                                value={selectedUserTagOptions}
                                loading={userTagsLoading}
                                getOptionLabel={option => option.tagName}
                                isOptionEqualToValue={(left, right) =>
                                    left.tagName.localeCompare(right.tagName, undefined, { sensitivity: 'accent' }) === 0
                                }
                                onChange={(_, value) => setUserTags(value.map(tag => tag.tagName))}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label="User tags (empty = all users)"
                                        helperText="Show only to users with at least one of these tags. Applies after login only."
                                    />
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            {...getTagProps({ index })}
                                            key={option.tagName}
                                            label={option.tagName}
                                        />
                                    ))
                                }
                            />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Button variant="contained" onClick={onSave}>Save</Button>
                    </Grid>
                </Grid>
            )}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent><Typography>{dialogMsg}</Typography></DialogContent>
                <DialogActions><Button onClick={() => setDialogOpen(false)}>OK</Button></DialogActions>
            </Dialog>
        </Stack>
    );
}
